// ReportSectionsEditor.js (cloud-admin-portal)
//
// Authoring UI for a pipeline's REPORT-SECTION manifest, shown in the OTS "Configure"
// modal. Controls which sections the pipeline's final HTML report includes, their order,
// titles/descriptions, enabled flag, and per-section params (e.g. thresholds).
//
// Emits the shape the backend persists + stages into each run:
//   { schema_version, title?, sections: [ { id, title?, description?, enabled, params:{} } ] }
// Empty (no sections) => the pipeline falls back to its own repo-default report config.
import React from "react";

const s = {
    wrap: { marginTop: 18 },
    row: { border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, marginBottom: 8, background: "#f8fafc" },
    head: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
    sel: { padding: "5px 7px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, background: "#fff" },
    inp: { padding: "5px 7px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13 },
    ta: { width: "100%", minHeight: 42, fontFamily: "monospace", fontSize: 12, padding: 6, border: "1px solid #cbd5e1", borderRadius: 6, boxSizing: "border-box", marginTop: 6 },
    taErr: { borderColor: "#ef4444", background: "#fef2f2" },
    x: { border: "1px solid #fca5a5", color: "#b91c1c", background: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 12, padding: "3px 8px" },
    mini: { border: "1px solid #cbd5e1", background: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 12, padding: "2px 7px" },
    add: { border: "1px solid #cbd5e1", background: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 13, padding: "5px 10px", marginTop: 4 },
    muted: { color: "#94a3b8", fontSize: 12 },
};

// Per-section params editor: keeps raw text locally so the admin can type freely; commits
// the parsed object up only when it's valid JSON (invalid → red, not committed).
function ParamsField({ value, onChange }) {
    const [raw, setRaw] = React.useState(() => JSON.stringify(value || {}, null, 0));
    const [bad, setBad] = React.useState(false);
    React.useEffect(() => { setRaw(JSON.stringify(value || {}, null, 0)); /* eslint-disable-next-line */ }, []);
    return (
        <textarea
            style={{ ...s.ta, ...(bad ? s.taErr : {}) }}
            value={raw}
            placeholder='params JSON e.g. {"min_genes":200,"max_pct_mt":20}'
            onChange={(e) => {
                const t = e.target.value; setRaw(t);
                try { const o = JSON.parse(t || "{}"); if (o && typeof o === "object" && !Array.isArray(o)) { setBad(false); onChange(o); } else setBad(true); }
                catch (_) { setBad(true); }
            }}
        />
    );
}

export default function ReportSectionsEditor({ config, setConfig }) {
    const sections = (config && Array.isArray(config.sections)) ? config.sections : [];
    const setSections = (next) => setConfig({ schema_version: (config && config.schema_version) || "1.0", title: config && config.title, sections: next });

    const upd = (i, k, v) => setSections(sections.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
    const add = () => setSections([...sections, { id: "", title: "", description: "", enabled: true, params: {} }]);
    const remove = (i) => setSections(sections.filter((_, idx) => idx !== i));
    const move = (i, d) => {
        const j = i + d; if (j < 0 || j >= sections.length) return;
        const next = sections.slice(); [next[i], next[j]] = [next[j], next[i]]; setSections(next);
    };

    return (
        <div style={s.wrap}>
            <label style={{ display: "block", fontSize: 12, color: "#475569", margin: "10px 0 2px", fontWeight: 600 }}>Report sections (optional)</label>
            <p style={{ ...s.muted, marginTop: 0 }}>
                Control which sections the final report shows, their order and settings. Rendered in this order; disabled
                sections are skipped. Leave empty to use the pipeline's built-in default report.
            </p>

            {sections.length === 0 && <p style={s.muted}>No custom sections — the pipeline's default report is used. Add a section to override.</p>}

            {sections.map((sec, i) => (
                <div key={i} style={s.row}>
                    <div style={s.head}>
                        <button style={s.mini} onClick={() => move(i, -1)} disabled={i === 0} title="Move up">↑</button>
                        <button style={s.mini} onClick={() => move(i, 1)} disabled={i === sections.length - 1} title="Move down">↓</button>
                        <label style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <input type="checkbox" checked={sec.enabled !== false} onChange={(e) => upd(i, "enabled", e.target.checked)} /> enabled
                        </label>
                        <input style={{ ...s.inp, width: 150 }} value={sec.id || ""} placeholder="id (e.g. dimred)" onChange={(e) => upd(i, "id", e.target.value)} />
                        <input style={{ ...s.inp, flex: "1 1 180px", minWidth: 140 }} value={sec.title || ""} placeholder="Section title" onChange={(e) => upd(i, "title", e.target.value)} />
                        <button style={{ ...s.x, marginLeft: "auto" }} onClick={() => remove(i)}>Remove</button>
                    </div>
                    <input style={{ ...s.inp, width: "100%", boxSizing: "border-box", marginTop: 6 }} value={sec.description || ""} placeholder="Description (shown in the report, optional)" onChange={(e) => upd(i, "description", e.target.value)} />
                    <ParamsField value={sec.params} onChange={(o) => upd(i, "params", o)} />
                </div>
            ))}

            <button style={s.add} onClick={add}>+ Add section</button>
        </div>
    );
}
